/**
 * Formatteringsfunktioner för kalkyl-systemet
 */

/**
 * Formatera belopp som svensk valuta
 * @param {number} amount - Belopp att formatera
 * @returns {string} Formaterat belopp (t.ex. "12 345 kr")
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '0 kr';
  }
  
  return new Intl.NumberFormat('sv-SE', { 
    style: 'currency', 
    currency: 'SEK',
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * MAV-avrundning (Matematisk avrundning där 0.5 avrundas NEDÅT)
 * @param {number} value - Värdet att avrunda
 * @returns {number} - MAV-avrundat värde
 */
export const mavRound = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return 0;
  }
  
  // MAV-avrundning: 0.5 avrundas NEDÅT
  // Om decimaldelen är exakt 0.5, avrunda nedåt, annars använd vanlig avrundning
  const decimal = value - Math.floor(value);
  
  if (decimal === 0.5) {
    return Math.floor(value); // Avrunda nedåt vid exakt 0.5
  } else {
    return Math.round(value); // Vanlig avrundning för allt annat
  }
};

/**
 * Formatera belopp som svensk valuta MED MAV-avrundning
 * @param {number} amount - Belopp att formatera
 * @returns {string} Formaterat belopp (t.ex. "12 345 kr")
 */
export const formatCurrencyMAV = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '0 kr';
  }
  
  // MAV-avrunda först till heltal
  const rounded = mavRound(amount);
  
  // Formatera det avrundade heltalet (Intl kommer INTE avrunda igen eftersom det är heltal)
  return new Intl.NumberFormat('sv-SE', { 
    style: 'currency', 
    currency: 'SEK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(rounded);
};

/**
 * Formatera nummer med svensk nummerformat
 * @param {number} number - Nummer att formatera
 * @param {number} decimals - Antal decimaler
 * @returns {string} Formaterat nummer
 */
export const formatNumber = (number, decimals = 2) => {
  if (number === null || number === undefined || isNaN(number)) {
    return '0';
  }
  
  return new Intl.NumberFormat('sv-SE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(number);
};

/**
 * Formatera hierarkinivå till läsbar text
 * @param {string} level - Hierarkinivå ('main', 'sub', 'content')
 * @returns {string} Läsbar text
 */
export const formatHierarchyLevel = (level) => {
  const levels = {
    'main': 'Huvudrubrik',
    'sub': 'Underrubrik',
    'content': 'Innehåll'
  };
  return levels[level] || level;
};

/**
 * Formatera yrkesgrupp till läsbar text
 * @param {string} yrkesgrupp - Yrkesgrupp key
 * @returns {string} Läsbar text
 */
export const formatYrkesgrupp = (yrkesgrupp) => {
  const yrkesgrupper = {
    // Admin
    arbetsledning: 'Arbetsledning',
    framkorning: 'Framkörning',
    
    // Riv
    rivning: 'Rivning',
    bystning: 'Bystning',
    grovstad: 'Grovstäd',
    
    // Bygg
    byggnation: 'Byggnation',
    snicken: 'Snicken',
    snickeri: 'Snickeri',
    fonstermontage: 'Fönstermontage',
    fonsterfomontage: 'Fönster/Dörrmontage',
    fonsterdorrmontage: 'Fönster/Dörrmontage',
    grundarbete: 'Grundarbete',
    stomarbete: 'Stomarbete',
    murarbete: 'Murarbete',
    murmaterial: 'Murmaterial',
    takarbete: 'Takarbete',
    platarbete: 'Plåtarbete',
    
    // Måleri
    underarbete: 'Underarbete',
    finisharbete: 'Finisharbete',
    
    // Golv
    nota: 'Nota',
    slipalack: 'Slipa (lack)',
    slipahardvax: 'Slipa (hårdvax)',
    slipaluta: 'Slipa (luta)',
    slipagrov: 'Slipa (grov)',
    fjadrensparkett: 'Fjädernsparkett',
    massivparkettfallandel: 'Massiv parkett (fallande L)',
    mattlaggning: 'Mattläggning',
    
    // Plattsättning
    tatskikt: 'Tätskikt',
    flytspackling: 'Flytspackling',
    plattsattare: 'Plattsättare',
    
    // El
    elektriker: 'Elektriker',
    
    // VVS
    vvsinstallator: 'VVS-installatör',
    vvsinstallatorejrot: 'VVS-installatör (Ej ROT)',
    
    // Specialister
    gasinstallator: 'Gasinstallatör',
    oppengrupp1: 'Öppen grupp 1',
    oppengrupp2: 'Öppen grupp 2',
    
    // Specialister (Ej ROT)
    pumpbil: 'Pumpbil',
    stallning: 'Ställning',
    krantbil: 'Krantbil',
    kranbil: 'Kranbil',
    markanbud: 'Markanbud',
    oppengrupp1ejrot: 'Öppen grupp 1 (Ej ROT)',
    oppengrupp2ejrot: 'Öppen grupp 2 (Ej ROT)',
    
    // Försäkring
    forsakring: 'Försäkring',
    
    // Övrigt
    besiktningsman: 'Besiktningsman',
    finstad: 'Finstäd',
    sophantering: 'Sophantering'
  };
  
  return yrkesgrupper[yrkesgrupp] || yrkesgrupper[yrkesgrupp?.toLowerCase()] || yrkesgrupp;
};

/**
 * Formatera enhet
 * @param {string} enhet - Enhet (h, st, kvm, etc.)
 * @returns {string} Formaterad enhet
 */
export const formatEnhet = (enhet) => {
  const enheter = {
    'h': 'timmar',
    'st': 'st',
    'kvm': 'm²',
    'm2': 'm²',
    'meter': 'm',
    'm': 'm'
  };
  
  return enheter[enhet?.toLowerCase()] || enhet || '';
};

/**
 * Formatera procentsats
 * @param {number} percent - Procentsats
 * @returns {string} Formaterad procent
 */
export const formatPercent = (percent) => {
  if (percent === null || percent === undefined || isNaN(percent)) {
    return '0%';
  }
  
  return `${formatNumber(percent, 1)}%`;
};

/**
 * Hämta display-namn för yrkesgrupp (alias för formatYrkesgrupp)
 * @param {string} key - Yrkesgrupp key
 * @returns {string} - Display-namn
 */
export const getYrkesgruppDisplayName = formatYrkesgrupp;

